// App.js - Modificato per supportare AdminOverlay
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import Player from "./Player";
import { Accordion } from "./Accordion";
import FamilySelector from "./FamilySelector";
import FamilyAccordion from "./FamilyAccordion";
import { FaChevronRight, FaCog } from "react-icons/fa";
import logo from "./assets/logo-dieffebi.svg";

// Import material manager
import { materialManager } from "./materialManager";

// Import configuration functions
import { switchConfiguration } from "./config";

// Import API functions
import { fetchProductByCode, fetchProducts, fetchMaterialLinks } from "./api";

// Import Admin Overlay (NUOVO)
import AdminOverlay from "./AdminOverlay";
import ContactModal from "./ContactModal";
import { getTranslations } from "./components/translations";

// Debug utility
import { debug } from "./debug";

// Componente LoadingScreen separato per maggiore chiarezza
const LoadingScreen = ({ isVisible }) => {
  if (!isVisible) return null;

  return (
    <div
      className="ay-w-full ay-h-screen ay-bg-white ay-opacity-95 ay-fixed ay-top-0 ay-left-0 ay-z-50 ay-flex ay-justify-center ay-items-center ay-text-3xl"
      id="loader"
    >
      <img src={logo} alt="Logo dieffebi" className="ay-animate-scale-pulse" />
    </div>
  );
};

// MODIFIED: Converted to forwardRef with useImperativeHandle
const App = forwardRef(({ productId, isAdmin = false, language }, ref) => {
  // Loading state - dividiamo il caricamento in due fasi distinte
  const [initialLoading, setInitialLoading] = useState(true); // Caricamento iniziale dell'app
  const [productLoading, setProductLoading] = useState(false); // Caricamento dei prodotti

  // NEW: State for language with initial value from props
  const [currentLanguage, setCurrentLanguage] = useState(language);

  // NEW: Admin overlay state
  const [isAdminOverlayVisible, setIsAdminOverlayVisible] = useState(isAdmin);
  const [formScreenshotBase64, setFormScreenshotBase64] = useState(null);

  // NEW: Expose updateLanguage method to parent via ref
  useImperativeHandle(ref, () => ({
    updateLanguage: (newLanguage) => {
      debug.app(`Language update: ${newLanguage}`);
      setCurrentLanguage(newLanguage);
    },
  }));

  // Stato combinato di caricamento per il rendering
  const isLoading = initialLoading || productLoading;

  // Product state
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [selectedProductCode, setSelectedProductCode] = useState(productId);

  // Material state
  const [selectedMaterials, setSelectedMaterials] = useState({});
  const [viewerMaterials, setViewerMaterials] = useState([]);
  const [materialLinks, setMaterialLinks] = useState([]);

  // UI state
  const [isAccordionHidden, setIsAccordionHidden] = useState(false);
  // COMMENTATO - logica del 15% rimossa temporaneamente
  // const [priceModification, setPriceModification] = useState(0)
  // const [showOptionalMaterialNotice, setShowOptionalMaterialNotice] = useState(false)
  const [configurableMaterialOnClick, setConfigurableMaterialOnClick] =
    useState(null);
  // COMMENTATO - tracciamento materiali opzionali per pricing rimosso temporaneamente
  // const [selectedOptionalMaterials, setSelectedOptionalMaterials] = useState([])
  const [showContactModal, setShowContactModal] = useState(false);

  // Refs to keep track of selections
  const lastSelectedFamilyRef = useRef(null);
  const currentProductCodeRef = useRef(productId);
  const viewerLoadingRef = useRef(false);

  // Monitor riferimento allo stato corrente di caricamento
  const loadingStateRef = useRef(false);

  // NEW: Admin overlay management functions
  const openAdminOverlay = () => setIsAdminOverlayVisible(true);
  const closeAdminOverlay = () => setIsAdminOverlayVisible(false);

  // NEW: Keyboard shortcut per aprire admin overlay (Ctrl+Shift+A)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === "A") {
        e.preventDefault();
        setIsAdminOverlayVisible((prev) => !prev);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Fetch initial data on mount
  useEffect(() => {
    const initializeApp = async () => {
      setInitialLoading(true);
      loadingStateRef.current = true;

      try {
        await loadProducts();
        await loadMaterialLinks();
        materialManager.initializeAllMaterials();
      } catch (error) {
        debug.warn.app("Failed to initialize app:", error);
      } finally {
        // App inizializzata, ma potremmo ancora caricare il prodotto
        setInitialLoading(false);

        // Non terminiamo completamente il caricamento qui, lo faremo dopo il caricamento del prodotto
        if (!productLoading) {
          loadingStateRef.current = false;
        }
      }
    };

    initializeApp();

    // Add event listener for contact modal
    window.addEventListener("openContactModal", () =>
      setShowContactModal(true)
    );

    return () => {
      window.removeEventListener("openContactModal", () =>
        setShowContactModal(true)
      );
    };
  }, []);

  // Keep lastSelectedFamilyRef updated with selectedFamily
  useEffect(() => {
    if (selectedFamily) {
      lastSelectedFamilyRef.current = selectedFamily;
    }
  }, [selectedFamily]);

  // Initialize Emersya and load product data
  useEffect(() => {
    // Set up the initial family value in the ref
    if (selectedFamily) {
      lastSelectedFamilyRef.current = selectedFamily;
    }

    // Add Emersya initialization event listener
    document.addEventListener(
      "emersyaViewerInitialized",
      initializeEmersyaAPI,
      false
    );

    // Load product data if we have a product ID
    if (selectedProductCode) {
      loadProductData();
    }

    // Cleanup
    return () => {
      document.removeEventListener(
        "emersyaViewerInitialized",
        initializeEmersyaAPI
      );
    };
  }, []);

  // Reload product data when selected product changes
  useEffect(() => {
    if (selectedProductCode) {
      // Update our ref to track the current product code
      currentProductCodeRef.current = selectedProductCode;

      // Clear selected materials when product changes
      setSelectedMaterials({});
      // COMMENTATO - tracciamento materiali opzionali rimosso temporaneamente
      // setSelectedOptionalMaterials([])
      // setPriceModification(0)
      // setShowOptionalMaterialNotice(false)

      // Reset viewer loading flag when changing products
      viewerLoadingRef.current = false;

      // IMPORTANTE: Attiva il loading quando il prodotto cambia
      setProductLoading(true);
      loadingStateRef.current = true;
      debug.app("Product changed, setting loading state");

      loadProductData();
    }
  }, [selectedProductCode]);

  // Load all products
  const loadProducts = async () => {
    try {
      const productsData = await fetchProducts();
      setProducts(productsData);
      return productsData;
    } catch (error) {
      debug.warn.app("Failed to fetch products:", error);
      return [];
    }
  };

  // Load all material links
  const loadMaterialLinks = async () => {
    try {
      const links = await fetchMaterialLinks();
      setMaterialLinks(links);
      return links;
    } catch (error) {
      debug.warn.app("Failed to load material links:", error);
      return [];
    }
  };

  // Toggle accordion visibility
  const toggleAccordionVisibility = () => {
    setIsAccordionHidden((prevState) => !prevState);
    setTimeout(() => {
      if (window.emViewers && window.emViewers["emersyaIframe"]) {
        window.emViewers["emersyaIframe"].resize();
      }
    }, 150);
  };

  // Load product data
  const loadProductData = async () => {
    /*   console.log("Loading product data for:", selectedProductCode) */
    // Già impostato nel cambio di prodotto, ma assicuriamoci
    setProductLoading(true);
    loadingStateRef.current = true;

    try {
      const productData = await fetchProductByCode(selectedProductCode);

      // Set the product data
      setSelectedProduct(productData);

      const familyName = productData._family;

      // Update family if needed
      if (familyName) {
        setSelectedFamily(familyName);
        // Update the ref as well
        lastSelectedFamilyRef.current = familyName;
      }

      debug.app("Loaded product:", productData.code);

      // Extract family defaults
      let familyDefaults = productData._familyDefaults?.defaultMaterials || {};
      let familyTypeLinks = productData._familyDefaults?.typeLinks || [];
      let familyCollectionTranslations =
        productData._familyDefaults?.collectionTranslations || {};

      // Ensure each material type has both standard and optional arrays for compatibility
      Object.keys(familyDefaults).forEach((type) => {
        if (!familyDefaults[type].standard) familyDefaults[type].standard = [];
        if (!familyDefaults[type].optional) familyDefaults[type].optional = [];
      });

      // Store family material settings in material manager
      if (familyName) {
        // Pass both types, typeLinks, and collectionTranslations
        materialManager.setFamilySettings(familyName, {
          types: familyDefaults,
          typeLinks: familyTypeLinks,
          collectionTranslations: familyCollectionTranslations,
        });
      }

      // If product has custom material settings, ensure they have standard and optional arrays
      if (
        productData.materials &&
        Object.keys(productData.materials).length > 0
      ) {
        const productMaterials = { ...productData.materials };

        Object.keys(productMaterials).forEach((type) => {
          if (!productMaterials[type].standard)
            productMaterials[type].standard = [];
          if (!productMaterials[type].optional)
            productMaterials[type].optional = [];
        });

        materialManager.setProductSettings(productData.code, {
          types: productMaterials,
          typeLinks: productData.typeLinks || [],
        });
      }
    } catch (error) {
      debug.warn.app("Failed to load product:", error);
      // In caso di errore, terminiamo il caricamento
      setProductLoading(false);
      loadingStateRef.current = false;
    }
  };

  // Initialize Emersya API
  const initializeEmersyaAPI = () => {
    const emersyaViewer = window.emViewers["emersyaIframe"];

    // Add configurableMaterial highlight listener
    emersyaViewer.addEventListener(
      "onConfigurableMaterialHighlight",
      function (event) {
        if (event[0]) {
          setConfigurableMaterialOnClick(event[0]);
        } else {
          setConfigurableMaterialOnClick(null);
        }
      }
    );

    // Add stateChange listener to initialize materials when viewer is ready
    emersyaViewer.addEventListener("onStateChange", async (data) => {
      if (data.viewerState === "loaded") {
        // Prevent duplicate executions for the same product
        if (viewerLoadingRef.current) {
          return;
        }

        // Set loading flag
        viewerLoadingRef.current = true;

        // Assicuriamoci che il loading sia attivo
        if (!loadingStateRef.current) {
          setProductLoading(true);
          loadingStateRef.current = true;
          /*    console.log(
            "Ensuring loading state is active during viewer initialization"
          ) */
        }

        try {
          // IMPORTANT: Use the current product code from the ref, not the state
          // This ensures we're always using the most recently selected product
          const currentCode = currentProductCodeRef.current;

          // Load fresh product data directly using the current code
          // This avoids any stale references in the state
          const productData = await fetchProductByCode(currentCode);

          // Update the selectedProduct state with this fresh data
          setSelectedProduct(productData);

          // Also ensure the selectedProductCode state is synchronized
          if (currentCode !== selectedProductCode) {
            setSelectedProductCode(currentCode);
          }

          // Get the family from the product
          const productFamily = productData._family;

          // CRITICAL: First, make sure we have the correct family settings
          // Step 1: Always store the product's native family settings under its own name
          const productFamilyDefaults =
            productData._familyDefaults?.defaultMaterials || {};
          const productFamilyTypeLinks =
            productData._familyDefaults?.typeLinks || [];
          const productFamilyCollectionTranslations =
            productData._familyDefaults?.collectionTranslations || {};

          // Always save product's own family settings
          if (productFamily) {
            materialManager.setFamilySettings(productFamily, {
              types: productFamilyDefaults,
              typeLinks: productFamilyTypeLinks,
              collectionTranslations: productFamilyCollectionTranslations,
            });
          }

          // Step 2: Determine which family we should actually use (UI-selected or product's family)
          const actualFamily = lastSelectedFamilyRef.current || productFamily;

          if (
            lastSelectedFamilyRef.current &&
            lastSelectedFamilyRef.current !== productFamily
          ) {
            debug.app(`Using UI family: ${lastSelectedFamilyRef.current} vs product: ${productFamily}`);
          } else {
            // Update UI state only if no explicit selection or it matches product family
            if (actualFamily !== selectedFamily) {
              /*        console.log(`Updating family to match product: ${productFamily}`) */
              setSelectedFamily(productFamily);
              lastSelectedFamilyRef.current = productFamily;
            }
          }

          /*   console.log(
            "Emersya viewer loaded, using family:",
            actualFamily,
            "with product:",
            currentCode
          ) */

          // Step 3: Find the correct family settings for the selected family (if different from product family)
          let actualFamilyDefaults = {};
          let actualFamilyTypeLinks = [];
          let actualFamilyCollectionTranslations = {};

          if (actualFamily !== productFamily) {
            // We need to find settings for the selected family from other products
            try {
              // Try to get the settings by searching through all other products
              const familyProducts = products.filter(
                (p) =>
                  (p._family === actualFamily || p.family === actualFamily) &&
                  p._familyDefaults &&
                  p._familyDefaults.defaultMaterials
              );

              if (familyProducts.length > 0) {
                const familyProduct = familyProducts[0];
                actualFamilyDefaults =
                  familyProduct._familyDefaults.defaultMaterials || {};
                actualFamilyTypeLinks =
                  familyProduct._familyDefaults.typeLinks || [];
                actualFamilyCollectionTranslations =
                  familyProduct._familyDefaults.collectionTranslations || {};
              } else {
                debug.warn.app(
                  `No products found with settings for family ${actualFamily}`
                );
              }
            } catch (err) {
              debug.warn.app(
                `Failed to load settings for family ${actualFamily}:`,
                err
              );
            }
          } else {
            // If we're using the product's own family, use its settings
            actualFamilyDefaults = productFamilyDefaults;
            actualFamilyTypeLinks = productFamilyTypeLinks;
            actualFamilyCollectionTranslations =
              productFamilyCollectionTranslations;
          }

          // CRITICAL: Now that we have the correct settings, update the material manager
          if (actualFamily) {
            materialManager.setFamilySettings(actualFamily, {
              types: actualFamilyDefaults,
              typeLinks: actualFamilyTypeLinks,
              collectionTranslations: actualFamilyCollectionTranslations,
            });
          }

          // Get the current global configuration
          const globalConfig =
            await emersyaViewer.getCurrentGlobalConfiguration();

          // Get the current project data
          const currentProject = Object.keys(globalConfig.projectsData)[0];
          const projectData = globalConfig.projectsData[currentProject];

          // Get configurable materials from the viewer
          const configurableMaterials =
            await emersyaViewer.getProductNodeInstanceConfigurableMaterials({
              localId: globalConfig.sceneGraph.localId,
            });

          // Initialize material manager with viewer materials - using correct family and product code
          // CRITICAL: Always use the code from the product data we just loaded
          /*  console.log(
            `Initializing material manager with correct family: ${actualFamily}, product: ${productData.code}`
          ) */

          materialManager.initialize(
            configurableMaterials,
            actualFamily,
            productData.code
          );

          // Get available materials with family/product settings applied
          // CRITICAL: Always use the code from the product data we just loaded
          const availableMaterials = materialManager.getAvailableMaterials(
            actualFamily,
            productData.code
          );
          debug.viewer("Configurable materials:", configurableMaterials.length);
          /*     console.log(
            "Enriched and overridden materials for product:",
            productData.code,
            availableMaterials
          ) */

          // Clear any previous material selections to avoid conflicts
          setSelectedMaterials({});
          // COMMENTATO - tracciamento materiali opzionali rimosso temporaneamente
          // setSelectedOptionalMaterials([])
          // setPriceModification(0)
          // setShowOptionalMaterialNotice(false)

          // Update viewer materials with the fresh data
          setViewerMaterials(availableMaterials);

          // Set default materials if available in the project data
          if (projectData?.materialTrees) {
            setDefaultMaterials(projectData);
          }

          // Set camera to front view and start rotation
          emersyaViewer.setViewpoint({
            name: "dfbfront",
            transitionTime: 500,
            reinitializeZoomLimits: true,
          });

          setTimeout(() => {
            emersyaViewer.play();
          }, 1500);

          // IMPORTANTE: terminiamo il caricamento dopo aver configurato tutto
          /*  console.log("Viewer initialization completed, ending loading state") */
          setProductLoading(false);
          loadingStateRef.current = false;

          // Reset loading flag to allow future initializations
          viewerLoadingRef.current = false;
        } catch (error) {
          debug.warn.app("Error initializing materials:", error);
          // IMPORTANTE: terminiamo il caricamento anche in caso di errore
          setProductLoading(false);
          loadingStateRef.current = false;
          viewerLoadingRef.current = false;
        }
      }
    });
  };

  // Set default materials from project data
  const setDefaultMaterials = (projectData) => {
    if (!projectData?.materialTrees) return;

    // Only set default materials if we don't already have selections
    // This prevents overriding user selections
    if (Object.keys(selectedMaterials).length > 0) {
      debug.materials("Keeping existing material selections");
      return;
    }

    debug.materials("Setting default materials for:", currentProductCodeRef.current);

    const newSelected = {};
    // COMMENTATO - tracciamento materiali opzionali rimosso temporaneamente
    // const newOptionalMaterials = []

    Object.entries(projectData.materialTrees).forEach(([key, value]) => {
      if (value) {
        // Extract code prefix if available (e.g., "V47" from "V47 Beige red")
        const prefixMatch = value.match(/^([A-Z]+\d+)\s+/);
        const codePrefix = prefixMatch ? prefixMatch[1] : null;

        // First try to find by exact name
        let material = materialManager.findMaterialByName(value);

        // If not found and we have a code prefix, try finding by code prefix
        if (!material && codePrefix) {
          material = materialManager.findMaterialByName(codePrefix);
        }

        // If still not found, use basic information
        if (material) {
          const selectedMaterial = {
            slug: material.slug || value,
            name: material.name || codePrefix || value,
            thumbnail: material.thumbnail || "",
            viewerName: value, // Important: Keep the exact name for the viewer
            standard: material.standard || false,
            optional: material.optional || false,
          };

          newSelected[key] = selectedMaterial;

          // COMMENTATO - tracciamento materiali opzionali rimosso temporaneamente
          // CHECK IF DEFAULT MATERIAL IS OPTIONAL
          // if (selectedMaterial.optional) {
          //   newOptionalMaterials.push({
          //     type: key,
          //     material: selectedMaterial,
          //   })
          // }
        } else {
          // If not found in materialManager, use as is (assume standard)
          newSelected[key] = {
            slug: value,
            name: codePrefix || value,
            thumbnail: "",
            viewerName: value,
            standard: true,
            optional: false,
          };
        }
      }
    });

    setSelectedMaterials(newSelected);

    // COMMENTATO - tracciamento materiali opzionali rimosso temporaneamente
    // UPDATE OPTIONAL MATERIALS TRACKING FOR DEFAULTS
    // setSelectedOptionalMaterials(newOptionalMaterials)

    // Position camera and start rotation
    const emersyaViewer = window.emViewers["emersyaIframe"];
    emersyaViewer.setViewpoint({
      name: "dfbfront",
      transitionTime: 500,
      reinitializeZoomLimits: true,
    });

    setTimeout(() => {
      emersyaViewer.play();
    }, 1500);
  };

  // Handle family change
  const handleFamilyChange = useCallback((newFamily) => {
    /* console.log(`Changing family to: ${newFamily}`) */

    // Important: Update the family state and ref BEFORE finding a product
    setSelectedFamily(newFamily);
    // Update the ref
    lastSelectedFamilyRef.current = newFamily;

    // DO NOT AUTO-SELECT PRODUCT (removed that functionality)
    // This allows the user to choose which product they want from the family

    // Clear material selections when family changes
    setSelectedMaterials({});
    // COMMENTATO - tracciamento materiali opzionali rimosso temporaneamente
    // setSelectedOptionalMaterials([])
    // setPriceModification(0)
    // setShowOptionalMaterialNotice(false)
  }, []);

  // Handle product change
  const handleProductChange = useCallback(
    (newProductCode) => {
      if (!newProductCode) return;
      /* console.log(`Changing product to: ${newProductCode}`) */

      // Prevent unnecessary re-renders if product is already selected
      if (newProductCode === selectedProductCode) {
        /* console.log("Product already selected, skipping change") */
        return;
      }

      // IMPORTANTE: Attiva il loading screen prima di cambiare prodotto
      setProductLoading(true);
      loadingStateRef.current = true;
      /* console.log("Product change initiated, setting loading state to true") */

      // Update our ref to track the current product code
      currentProductCodeRef.current = newProductCode;
      /* console.log(`Updated currentProductCodeRef to: ${newProductCode}`) */

      // Always reset material selections when product changes
      setSelectedMaterials({});
      // COMMENTATO - tracciamento materiali opzionali rimosso temporaneamente
      // setSelectedOptionalMaterials([])
      // setPriceModification(0)
      // setShowOptionalMaterialNotice(false)
      setConfigurableMaterialOnClick(null);

      // Reset viewer loading flag
      viewerLoadingRef.current = false;

      // Set the new product code first - this will trigger the loadProductData effect
      setSelectedProductCode(newProductCode);
    },
    [selectedProductCode]
  );

  // Handle material change
  const handleMaterialChange = useCallback(
    async (technicalName, newMaterial) => {
      /*  console.log(`Changing material for ${technicalName}:`, newMaterial.name)
      console.log(
        `Current family: ${selectedFamily}, Current product: ${selectedProductCode}`
      ) */

      // IMPORTANT: Force a small delay before processing to ensure all state is available
      // This helps with the first-click issue
      await new Promise((resolve) => setTimeout(resolve, 10));

      // Create a copy of the current materials state to work with
      const updatedMaterials = { ...selectedMaterials };

      // Add the initially selected material
      updatedMaterials[technicalName] = newMaterial;

      // Apply material change to the 3D model using switchConfiguration
      switchConfiguration(newMaterial, technicalName);

      // Remove any highlights
      if (window.emViewers && window.emViewers["emersyaIframe"]) {
        window.emViewers["emersyaIframe"].setHighlight({
          configurableMaterials: [""],
        });
      }

      // Check if we have valid family settings for the current family
      const currentFamilySettings =
        materialManager.familySettings[selectedFamily];

      if (!currentFamilySettings) {
        debug.warn.materials(`No family settings found for ${selectedFamily}`);
        // Try to find settings with case-insensitive match
        const lowerFamily = selectedFamily.toLowerCase();
        let foundMatch = false;

        for (const family in materialManager.familySettings) {
          if (family.toLowerCase() === lowerFamily) {
            /* debug.materials(`Found family settings with different case: ${family}`) */

            // Use these settings instead
            useTypeLinksFromFamily(
              family,
              technicalName,
              newMaterial,
              updatedMaterials
            );
            foundMatch = true;
            break;
          }
        }

        if (!foundMatch) {
          debug.warn.materials(
            `No family settings found for ${selectedFamily}, even with case-insensitive match`
          );
        }
      } else {
        // Use the correctly found family settings
        useTypeLinksFromFamily(
          selectedFamily,
          technicalName,
          newMaterial,
          updatedMaterials
        );
      }

      // Update the state with all changes at once
      setSelectedMaterials(updatedMaterials);

      // COMMENTATO - tracciamento materiali opzionali per pricing rimosso temporaneamente
      // Handle optional materials tracking for price modifications
      // updateOptionalMaterialsTracking(
      //   technicalName,
      //   newMaterial,
      //   updatedMaterials
      // )
    },
    [selectedFamily, selectedProductCode, selectedMaterials]
  );

  // Helper function to process type links for a family
  const useTypeLinksFromFamily = (
    family,
    technicalName,
    newMaterial,
    updatedMaterials
  ) => {
    // Get type links from family settings
    const typeLinks = materialManager.familySettings[family]?.typeLinks || [];

    // Keep track of how many linked materials we've scheduled
    let linkedMaterialCount = 0;

    // Track which types have been handled by specific mappings
    const typesHandledBySpecificMappings = new Set();

    // Helper function to normalize strings for comparison
    const normalize = (str) =>
      String(str || "")
        .toLowerCase()
        .replace(/\s+/g, "")
        .replace(/-/g, "");

    // STEP 1: Handle specific mappings first
    for (const link of typeLinks) {
      // Skip if this link doesn't include our type
      if (
        !link.linkedTechnicalNames ||
        !link.linkedTechnicalNames.includes(technicalName)
      ) {
        continue;
      }

      // Check if this link has specific mappings for our source type
      if (link.specificMappings && link.specificMappings[technicalName]) {
        const sourceMappings = link.specificMappings[technicalName];

        // For each target type in the specific mappings
        for (const targetType of Object.keys(sourceMappings)) {
          // Skip if not in the linkedTechnicalNames
          if (!link.linkedTechnicalNames.includes(targetType)) {
            continue;
          }

          // Find specific mapping for this material
          const mappedMaterialCode = materialManager.findSpecificMapping(
            family,
            technicalName,
            targetType,
            newMaterial.name
          );

          if (mappedMaterialCode) {
            debug.materials(`Specific mapping: ${newMaterial.name} → ${mappedMaterialCode} (${targetType})`);

            // Find the material type in viewer materials
            const targetTypeData = viewerMaterials.find(
              (m) => m.technicalName === targetType
            );

            if (targetTypeData) {
              // Find the mapped material in the target type
              let mappedMaterial = targetTypeData.variations.find(
                (m) =>
                  m.name === mappedMaterialCode ||
                  normalize(m.name) === normalize(mappedMaterialCode)
              );

              if (mappedMaterial) {
                debug.materials(`Applying mapping: ${mappedMaterial.name} to ${targetType}`);

                // Copy the material to avoid reference issues
                const linkedMaterial = { ...mappedMaterial };

                // IMPORTANT: Preserve the standard/optional status from the source material
                linkedMaterial.standard = newMaterial.standard;
                linkedMaterial.optional = newMaterial.optional;

                // Add to our updated materials batch
                updatedMaterials[targetType] = linkedMaterial;

                // Mark this type as handled
                typesHandledBySpecificMappings.add(targetType);

                // Increment counter and calculate delay
                linkedMaterialCount++;
                const delay = linkedMaterialCount * 100;

                // Apply to 3D model with the calculated delay
                switchConfiguration(linkedMaterial, targetType, delay);

                debug.materials(`Scheduled mapping: ${targetType} (${delay}ms delay)`);
              } else {
                debug.warn.materials(`Mapped material ${mappedMaterialCode} not found in ${targetType}`);
              }
            }
          }
        }
      }
    }

    // STEP 2: Handle normal typeLinks for remaining types
    for (const link of typeLinks) {
      // Skip if this link doesn't include our type
      if (
        !link.linkedTechnicalNames ||
        !link.linkedTechnicalNames.includes(technicalName)
      ) {
        continue;
      }

      // For each other type in this link
      for (const linkedType of link.linkedTechnicalNames) {
        // Skip the current type - we only want to update other types
        if (linkedType === technicalName) {
          continue;
        }

        // Skip if this type was already handled by specific mappings
        if (typesHandledBySpecificMappings.has(linkedType)) {
          debug.materials(`Skipping ${linkedType} - handled by specific mapping`);
          continue;
        }

        // Find the material type in viewer materials
        const linkedTypeData = viewerMaterials.find(
          (m) => m.technicalName === linkedType
        );
        if (!linkedTypeData) {
          /*  console.log(`Linked type ${linkedType} not found in viewer materials`) */
          continue;
        }

        // Try to find the same material by name in the linked type
        let matchingMaterial = null;

        // First try exact name match
        matchingMaterial = linkedTypeData.variations.find(
          (m) => m.name === newMaterial.name
        );

        // If no match, try normalized name match
        if (!matchingMaterial) {
          const normalizedNewMaterialName = normalize(newMaterial.name);
          matchingMaterial = linkedTypeData.variations.find(
            (m) => normalize(m.name) === normalizedNewMaterialName
          );
        }

        if (matchingMaterial) {
          /*    console.log(
            `Found matching material ${matchingMaterial.name} in type ${linkedType}`
          ) */

          // Copy the original material to avoid reference issues
          const linkedMaterial = { ...matchingMaterial };

          // IMPORTANT: Preserve the standard/optional status from the source material
          linkedMaterial.standard = newMaterial.standard;
          linkedMaterial.optional = newMaterial.optional;

          // Add to our updated materials batch
          updatedMaterials[linkedType] = linkedMaterial;

          // Increment counter and calculate delay
          linkedMaterialCount++;
          const delay = linkedMaterialCount * 100; // Incremental delay of 100ms per material

          // Apply to 3D model with the calculated delay
          switchConfiguration(linkedMaterial, linkedType, delay);
          /*  console.log(
            `Scheduled linked material for ${linkedType} with ${delay}ms delay`
          ) */
        } else {
          debug.warn.materials(`No matching material in type ${linkedType}`);
        }
      }
    }
  };

  // COMMENTATO - Helper per tracciamento materiali opzionali rimosso temporaneamente
  // Helper to update optional materials tracking
  // const updateOptionalMaterialsTracking = (
  //   technicalName,
  //   newMaterial,
  //   updatedMaterials
  // ) => {
  //   // Create a completely new array to track all currently selected optional materials
  //   const newOptionalMaterials = []

  //   // Process ALL materials in updatedMaterials (including the new one and linked ones)
  //   Object.entries(updatedMaterials).forEach(([type, material]) => {
  //     if (material && material.optional === true) {
  //       // Add to optional materials list
  //       newOptionalMaterials.push({
  //         type: type,
  //         material: material,
  //       })
  //     }
  //   })

  //   // Log for debugging
  //   console.log("Updated optional materials tracking:", newOptionalMaterials)

  //   // Update the state with the new complete list
  //   setSelectedOptionalMaterials(newOptionalMaterials)
  // }

  // COMMENTATO - Calcolo prezzo con materiali opzionali rimosso temporaneamente
  // Calculate price with optional materials
  // const calculateTotalPrice = useCallback(() => {
  //   if (!selectedProduct || !selectedProduct.price) return 0

  //   // If we have optional materials, add 15% to the price
  //   if (selectedOptionalMaterials.length > 0) {
  //     const basePrice = selectedProduct.price
  //     const surcharge = basePrice * 0.15
  //     return basePrice + surcharge
  //   }

  //   return selectedProduct.price
  // }, [selectedProduct, selectedOptionalMaterials])

  // COMMENTATO - Aggiornamento prezzo con materiali opzionali rimosso temporaneamente
  // Update price modification and notice when optional materials or product changes
  // useEffect(() => {
  //   // Ensure we have a valid product and price
  //   if (!selectedProduct || typeof selectedProduct.price !== "number") {
  //     setPriceModification(0)
  //     setShowOptionalMaterialNotice(false)
  //     return
  //   }

  //   // Calculate surcharge if we have optional materials
  //   if (selectedOptionalMaterials.length > 0) {
  //     const surcharge = selectedProduct.price * 0.15
  //     setPriceModification(surcharge)
  //     setShowOptionalMaterialNotice(true)
  //   } else {
  //     setPriceModification(0)
  //     setShowOptionalMaterialNotice(false)
  //   }
  // }, [selectedOptionalMaterials, selectedProduct])

  useEffect(() => {
    // Cleanup function when component unmounts
    return () => {
      debug.app("App component unmounting, cleaning up...");

      // Clean up Emersya listeners
      if (window.emViewers && window.emViewers["emersyaIframe"]) {
        try {
          // Remove event listeners if possible
          document.removeEventListener(
            "emersyaViewerInitialized",
            initializeEmersyaAPI
          );
        } catch (error) {
          debug.warn.app("Error cleaning up Emersya listeners:", error);
        }
      }

      // Clean up window event listeners
      window.removeEventListener("openContactModal", () =>
        setShowContactModal(true)
      );
    };
  }, []); // Empty dependency array means this runs only on mount/unmount

  // COMMENTATO - Chiusura automatica modal rimossa
  // useEffect(() => {
  //   if (showContactModal && selectedOptionalMaterials.length === 0) {
  //     setShowContactModal(false)
  //   }
  // }, [selectedOptionalMaterials, showContactModal])

  // COMMENTATO - Ricalcolo materiali opzionali rimosso temporaneamente
  // useEffect(() => {
  //   // Recalculate optional materials whenever selectedMaterials changes
  //   // This ensures we catch any materials that might have been missed
  //   const allOptionalMaterials = []

  //   Object.entries(selectedMaterials).forEach(([type, material]) => {
  //     if (material && material.optional === true) {
  //       allOptionalMaterials.push({
  //         type: type,
  //         material: material,
  //       })
  //     }
  //   })

  //   // Only update if the arrays are actually different
  //   const currentOptionalCount = selectedOptionalMaterials.length
  //   const newOptionalCount = allOptionalMaterials.length

  //   if (currentOptionalCount !== newOptionalCount) {
  //     console.log("Syncing optional materials from selectedMaterials change")
  //     setSelectedOptionalMaterials(allOptionalMaterials)
  //   }
  // }, [selectedMaterials])

  return (
    <div className="ay-text-sm ay-leading-[19px] ay-relative ay-z-30">
      {/* Loading screen component */}
      <LoadingScreen isVisible={isLoading} />

      {/* MODIFIED: App normale viene sempre renderizzata */}
      <div className="ay-grid ay-grid-cols-3 md:ay-flex-row ay-h-screen ay-overflow-hidden">
        <div
          className={`ay-relative ${
            isAccordionHidden
              ? "ay-col-span-3"
              : "ay-col-span-3 lg:ay-col-span-2"
          } ay-transition-all ay-duration-500 ay-ease-in-out`}
        >
          {/* 3D Viewer Component */}
          <Player
            selectedProductCode={selectedProductCode}
            selectedProduct={selectedProduct}
            selectedMaterials={selectedMaterials}
            viewerMaterials={viewerMaterials}
            language={currentLanguage}
            isAdmin={isAdmin}
            productName={
              selectedProduct?.lang?.it || selectedProduct?.name || ""
            }
          />

          {/* Toggle button for accordion */}
          <button
            onClick={toggleAccordionVisibility}
            className={`ay-absolute ay-transition-transform ay-duration-300 ${
              isAccordionHidden ? "ay-rotate-180" : ""
            } ay-right-4 ay-opacity-20 ay-top-1/2 ay-transform ay--translate-y-1/2 ay-text-3xl ay-font-bold ay-focus:ay-outline-none`}
          >
            <FaChevronRight />
          </button>

          {/* NEW: Floating admin button - visibile solo se non è già aperto l'overlay */}
          {!isAdminOverlayVisible && isAdmin && (
            <button
              onClick={openAdminOverlay}
              className="ay-fixed ay-bottom-4 ay-right-4 ay-z-30 ay-p-3 ay-bg-blue-600 ay-text-white ay-rounded-full ay-shadow-lg ay-hover:ay-bg-blue-700 ay-transition-colors ay-opacity-75 ay-hover:ay-opacity-100"
              title="Apri Admin Panel (Ctrl+Shift+A)"
            >
              <FaCog size={20} />
            </button>
          )}
        </div>

        {/* Configurator Panel */}
        <div
          className={`ay-p-4 ay-bg-white ay-overflow-auto lg:ay-px-12 ay-py-16 md:ay-py-12  ${
            isAccordionHidden
              ? "ay-col-span-3"
              : "ay-col-span-3 lg:ay-col-span-1"
          } ay-transition-all ay-duration-500 ay-ease-in-out`}
          style={{ display: isAccordionHidden ? "none" : "block" }}
        >
          {/* Product Family Selector - MODIFIED: using currentLanguage instead of language */}
          <FamilySelector
            products={products}
            selectedFamily={selectedFamily}
            onFamilyChange={handleFamilyChange}
            onProductChange={handleProductChange}
            selectedProduct={selectedProduct}
            language={currentLanguage}
          />

          {/* Product Selector (Family Accordion) - MODIFIED: using currentLanguage */}
          <FamilyAccordion
            products={products}
            selectedProduct={selectedProduct}
            onProductChange={handleProductChange}
            selectedFamily={selectedFamily}
            language={currentLanguage}
          />

          {/* Material Configuration Accordions */}
          <div>
            {/* Material Accordions - MODIFIED: using currentLanguage */}
            {getOrderedMaterials(
              viewerMaterials,
              selectedFamily,
              materialManager
            ).map((mat) => {
              // Get standard and optional materials for this type
              const standardMaterials = mat.variations
                .filter((v) => v.standard)
                .map((v) => v.slug);

              const optionalMaterials = mat.variations
                .filter((v) => v.optional)
                .map((v) => v.slug);

              return (
                <Accordion
                  key={mat.technicalName}
                  title={mat.technicalName}
                  matId={mat}
                  variations={mat.variations}
                  selected={selectedMaterials[mat.technicalName]}
                  onChange={(newMat) =>
                    handleMaterialChange(mat.technicalName, newMat)
                  }
                  language={currentLanguage}
                  standardMaterials={standardMaterials}
                  optionalMaterials={optionalMaterials}
                  collectionTranslations={mat.collectionTranslations || {}}
                  technicalTypeTranslations={
                    mat.technicalTypeTranslations || {}
                  }
                />
              );
            })}

            {/* Price information - AGGIORNATA: sempre "a partire da" e contatti */}
            {selectedProduct &&
              typeof selectedProduct.price === "number" &&
              selectedProduct.price > 0 && (
                <PriceInfo
                  key={`price-${selectedProduct.code}`}
                  selectedProduct={selectedProduct}
                  setShowContactModal={setShowContactModal}
                  language={currentLanguage}
                />
              )}
          </div>
        </div>
      </div>

      {/* NEW: Admin Overlay - Renderizzato condizionalmente */}
      {isAdminOverlayVisible && (
        <AdminOverlay
          selectedProductCode={selectedProductCode}
          onProductChange={handleProductChange}
          isVisible={isAdminOverlayVisible}
          onClose={closeAdminOverlay}
        />
      )}

      {/* Contact Modal - AGGIORNATO: props semplificate */}
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        selectedProduct={selectedProduct}
        selectedMaterials={selectedMaterials}
        viewerMaterials={viewerMaterials}
        language={currentLanguage}
        screenshotBase64={formScreenshotBase64}
        // pdfBase64={formPdfBase64} // per futuro, opzionale
      />
    </div>
  );
});

const getOrderedMaterials = (
  viewerMaterials,
  selectedFamily,
  materialManager
) => {
  // Get the default materials order for the current family
  const familySettings = materialManager.familySettings[selectedFamily];
  const defaultMaterials = familySettings?.types || {};

  // Get the order from defaultMaterials keys
  const defaultOrder = Object.keys(defaultMaterials);

  // Filter materials that have enough variations (>= 2) AND are not hidden
  const filteredMaterials = viewerMaterials.filter((mat) => {
    const hasEnoughVariations = mat.variations.length >= 2;

    // Check if this material type is hidden in defaultMaterials
    const materialConfig = defaultMaterials[mat.technicalName];
    const isHidden = materialConfig?.hidden === true;

    return hasEnoughVariations && !isHidden;
  });

  // Sort materials according to defaultMaterials order
  const orderedMaterials = filteredMaterials.sort((a, b) => {
    const aIndex = defaultOrder.indexOf(a.technicalName);
    const bIndex = defaultOrder.indexOf(b.technicalName);

    // If both are in defaultMaterials, sort by their order
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    // If only a is in defaultMaterials, a comes first
    if (aIndex !== -1 && bIndex === -1) {
      return -1;
    }

    // If only b is in defaultMaterials, b comes first
    if (aIndex === -1 && bIndex !== -1) {
      return 1;
    }

    // If neither is in defaultMaterials, maintain original order
    return 0;
  });

  return orderedMaterials;
};

// AGGIORNATO: PriceInfo component - Sempre "a partire da" + prezzo base + contatti
const PriceInfo = ({
  selectedProduct,
  setShowContactModal,
  language = "it",
}) => {
  // Get translations
  const translations = getTranslations(language);

  if (
    !selectedProduct ||
    typeof selectedProduct.price !== "number" ||
    selectedProduct.price <= 0
  )
    return null;

  const basePrice = selectedProduct.price;
  const isItalian = language === "it";

  return (
    <div className="ay-mb-4 ay-mt-10 ay-bg-white ay-rounded-md ay-overflow-hidden">
      {/* Intestazione del prezzo - SEMPRE "a partire da" */}
      <div className="ay-py-3 ay-border-b ay-border-gray-200 ay-bg-white">
        <div className="ay-flex ay-justify-between ay-items-center">
          <div>
            <p className="ay-font-medium ay-text-gray-700">
              {translations.priceInfo.estimate}
            </p>
            {isItalian && (
              <p className="ay-text-xs ay-text-gray-500 ay-mt-1">
                {translations.priceInfo.pricesWithoutVAT}
              </p>
            )}
          </div>
          <div className="ay-text-right">
            <p className="ay-text-xs ay-text-gray-500 ay-mb-1">
              {translations.priceInfo.startingFrom}
            </p>
            <p className="ay-text-xl ay-font-bold ay-text-gray-800">
              {basePrice.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* NUOVA SEZIONE: Informazioni di contatto */}
      <div className="ay-p-3 ay-bg-gray-50 ay-border-b ay-border-gray-200">
        <p className="ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-2">
          {translations.priceInfo.contactInfo}
        </p>
        <div className="ay-space-y-1">
          <p className="ay-text-sm ay-text-gray-600">
            <span className="ay-font-medium">Tel:</span>{" "}
            {translations.priceInfo.phone}
          </p>
          <p className="ay-text-sm ay-text-gray-600">
            <span className="ay-font-medium">Email:</span>{" "}
            {translations.priceInfo.emailSales}
          </p>
          <p className="ay-text-sm ay-text-gray-600">
            <span className="ay-font-medium">Marketing:</span>{" "}
            {translations.priceInfo.emailMarketing}
          </p>
        </div>
      </div>

      {/* Pulsante contatto - COMMENTED OUT come nel codice originale */}
      <div className="ay-p-3">
        <button
          onClick={() => setShowContactModal(true)}
          className="ay-text-[12px] ay-text-center 3xl:ay-text-[16px] ay-min-w-36 ay-border ay-border-black ay-px-6 ay-py-1 ay-bg-black ay-text-white ay-hover:ay-bg-white ay-hover:ay-text-black ay-disabled:ay-opacity-50 ay-flex ay-items-center ay-justify-center ay-gap-2 ay-transition-colors ay-focus:ay-outline-none ay-w-full"
        >
          {translations.priceInfo.contactUs}
        </button>
      </div>
    </div>
  );
};

export default App;
